import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
  type ConsentFormValues,
  schema,
} from '@/components/ApplePayExpressButton/schema';
import { Box } from '@/components/Box';
import { Button } from '@/components/Button';
import { NflMarketingConsentCheckbox } from '@/components/FormFields/NflMarketingConsentCheckbox/NflMarketingConsentCheckbox';
import * as CheckBoxWrapperStyle from '@/components/FormFields/RegisterMyWatchCheckbox/RegisterMyWatchCheckbox.styles';
import { FormProvider } from '@/components/FormProvider';
import { IconButton } from '@/components/IconButton';
import { Divider } from '@/components/Modal/Modal.styles';
import { TermsForm } from '@/components/TermsForm';
import { commonMessages } from '@/messages/common';

import * as S from './ApplePayExpressButton.style';

import { ReactComponent as ApplePayLogo } from '@/assets/icons/applepay-black.svg';

export const TermsConsentDialog = ({
  isOpen,
  onCancel,
  onConfirm,
  showNflMarketingOption = false,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (formData: ConsentFormValues) => void;
  showNflMarketingOption?: boolean;
}) => {
  const { formatMessage } = useIntl();

  const formCtx = useForm<ConsentFormValues>({
    defaultValues: schema(formatMessage).getDefault(),
    resolver: yupResolver(schema(formatMessage)),
  });

  return (
    <S.ModalWrapper
      gutters={true}
      isOpen={isOpen}
      maxWidth='sm'
      onClose={onCancel}
      width='100%'
    >
      <S.ModalHeader alignItems='center' justifyContent='center' p={1}>
        <ApplePayLogo height={22} />
        <Box style={{ position: 'absolute', right: 0 }}>
          <IconButton
            as='button'
            iconProps={{ name: 'close', size: 'm' }}
            onClick={onCancel}
            rel='noopener noreferrer'
            size='m'
            target='_blank'
            variant='ghosted'
          />
        </Box>
      </S.ModalHeader>
      <Divider />
      <FormProvider name='paymnt' onSubmit={onConfirm} {...formCtx}>
        <S.ModalBody flexDirection='column' gridGap={3}>
          <TermsForm action={formatMessage(commonMessages.confirm)} />
          {showNflMarketingOption && (
            <Box>
              <CheckBoxWrapperStyle.Wrapper $borderless>
                <NflMarketingConsentCheckbox />
              </CheckBoxWrapperStyle.Wrapper>
            </Box>
          )}
        </S.ModalBody>
        <S.ModalFooter gridGap={2} p={2}>
          <Button data-testid='ConfirmButton' type='submit'>
            {formatMessage(commonMessages.confirm)}
          </Button>
          <Button
            data-testid='CancelButton'
            onClick={onCancel}
            variant='outlined'
          >
            {formatMessage(commonMessages.cancel)}
          </Button>
        </S.ModalFooter>
      </FormProvider>
    </S.ModalWrapper>
  );
};
